import { useRef, useState } from 'react';
import Cropper from 'react-easy-crop';

import config from '@/config';
import getCroppedImgData from '@/pages/artwork/artwork-header-mobile/options/download-template/functions/cropImage';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import BrushCropIcon from '@/shared-components/icons/components/channel/BrushCropIcon';
import CircleCropIcon from '@/shared-components/icons/components/channel/CircleCropIcon';
import RectCropIcon from '@/shared-components/icons/components/channel/RectCropIcon';

import BrushCrop from './BrushCrop';

import './styles/StickerCropper.css';

export default function StickerCropper({ onNext, source }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  // Updated: Persist cropShape and cropMode using sessionStorage to maintain state across view changes.
  const [cropShape, setCropShape] = useState(() => {
    return sessionStorage.getItem('cropShape') || 'rect';
  });
  const [cropMode, setCropMode] = useState(() => {
    return sessionStorage.getItem('cropMode') || 'standard';
  });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [brushData, setBrushData] = useState([]);
  const [brushValidationError, setBrushValidationError] = useState('');
  const brushCropRef = useRef(null);

  const aspectRatio = 1;
  const isBlobURL = source && (source.startsWith('blob:') || source.startsWith('data:image/'));
  const imageUrl = isBlobURL
    ? source
    : `${config.app.imageServiceDomain}/${source}/w=2000`;

  const onCropComplete = (croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  };

  const handleStandardCrop = async () => {
    if (!source || !croppedAreaPixels) return null;
    try {
      const blobFile = await getCroppedImgData(imageUrl, croppedAreaPixels);
      const file = new File([blobFile], 'sticker.png', { type: 'image/png' });
      return file;
    } catch (error) {
      console.error('Error cropping sticker:', error);
      return null;
    }
  };

  const handleBrushCrop = async () => {
    if (!brushCropRef.current) {
      console.error('[StickerCropper] brushCropRef.current is null');
      return null;
    }

    try {
      const blob = await brushCropRef.current.getBrushImage();
      return blob;
    } catch (error) {
      console.error('[StickerCropper] Error capturing brush image:', error);
      return null;
    }
  };

  const handleNextClick = async () => {
    if (cropMode === 'brush' && brushData.length === 0) {
      setBrushValidationError('Select the area to continue');
      return;
    }

    setBrushValidationError('');
    let croppedResult = null;
    if (cropMode === 'standard') {
      croppedResult = await handleStandardCrop();
    } else {
      croppedResult = await handleBrushCrop();
    }

    onNext?.({
      croppedSticker: croppedResult,
      cropShape: cropMode === 'standard' ? cropShape : 'brush',
    });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(1, zoom + direction);
    setZoom(newZoom);
  };

  return (
    <div className="sticker-cropper">
      <div className="sticker-cropper__content" onWheel={handleWheel}>
        {imageUrl ? (
          cropMode === 'standard' ? (
            <div className={`container-wheel-cropper ${cropShape === 'circle' ? 'crop-circle' : 'crop-rect'}`}>
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                cropShape={cropShape === 'circle' ? 'round' : 'rect'}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={true}
              />
            </div>
          ) : (
            <BrushCrop
              ref={brushCropRef}
              source={imageUrl}
              onBrushDataChange={(lines) => {
                setBrushData(Array.isArray(lines) ? lines : []);
                if (Array.isArray(lines) && lines.length > 0) {
                  setBrushValidationError('');
                }
              }}
              brushStrokeWidth={40}  // 5 times bigger than before
            />
          )
        ) : (
          <div className="sticker-cropper__placeholder">No image available</div>
        )}
      </div>
      
      <footer className="sticker-cropper__footer">
        <div className="sticker-cropper__icons-container">
          <div
            className={`sticker-cropper__icon-button ${cropMode === 'standard' && cropShape === 'circle' ? 'active' : ''}`}
            onClick={() => {
              setCropMode('standard');
              setCropShape('circle');
              setBrushValidationError('');
              sessionStorage.setItem('cropMode', 'standard');
              sessionStorage.setItem('cropShape', 'circle');
            }}
          >
            <CircleCropIcon className="sticker-cropper__icon" />
          </div>

          <div
            className={`sticker-cropper__icon-button ${cropMode === 'standard' && cropShape === 'rect' ? 'active' : ''}`}
            onClick={() => {
              setCropMode('standard');
              setCropShape('rect');
              setBrushValidationError('');
              sessionStorage.setItem('cropMode', 'standard');
              sessionStorage.setItem('cropShape', 'rect');
            }}
          >
            <RectCropIcon className="sticker-cropper__icon" />
          </div>

          <div
            className={`sticker-cropper__icon-button ${cropMode === 'brush' ? 'active' : ''}`}
            onClick={() => {
              setCropMode('brush');
              setBrushData([]);
              sessionStorage.setItem('cropMode', 'brush');
            }}
          >
            <BrushCropIcon className="sticker-cropper__icon" />
          </div>
        </div>
        <div className="sticker-cropper__action">
          <ContinueButton onClick={handleNextClick}>Next</ContinueButton>
          {cropMode === 'brush' && brushValidationError ? (
            <p className="sticker-cropper__validation-error">{brushValidationError}</p>
          ) : null}
        </div>
      </footer>
    </div>
  );
}
